if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      abortController = undefined;
      onVariantChangeUnsubscriber = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];
      cartUpdateUnsubscriber = undefined;

      constructor() {
        super();
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get productId() {
        return this.getAttribute('data-product-id');
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get productForm() {
        return this.querySelector('form[is="product-form"]');
      }

      get quantityInput() {
        return this.querySelector('quantity-input input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = FoxTheme.pubsub.subscribe(
          FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();

        this.currentVariant = this.getSelectedVariant(this);
        const productMedia = this.querySelector(`[id^="MediaGallery-${this.dataset.section}"]`);
        const hasPdpMedia = productMedia?.dataset.hasPdpMedia === 'true';
        const hasExplicitVariantInUrl = Boolean(new URLSearchParams(window.location.search).get('variant'));
        if (this.currentVariant && (!hasPdpMedia || hasExplicitVariantInUrl)) {
          this.updateMedia(this.currentVariant);
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      getFamilyProductMap() {
        const el = this.querySelector('[data-family-product-map]');
        if (!el) return null;
        const str = el.getAttribute('data-family-product-map');
        if (!str) return {};
        const map = {};
        str.split(',,,').forEach((part) => {
          if (!part) return;
          const idx = part.indexOf(':::');
          if (idx === -1) return;
          const key = part.slice(0, idx);
          const rest = part.slice(idx + 3);
          const parts = rest.split('|||');
          const url = parts[0] || '';
          const handle = parts[1] || '';
          map[key] = {
            url: url || (handle ? `/products/${handle}` : ''),
            handle,
            id: parts[2] ? parseInt(parts[2], 10) : null
          };
        });

        
        return map;
      }

      getCurrentVariantOptionValues() {
        const script = this.querySelector('variant-selects [data-selected-variant]');
        if (!script || !script.innerHTML) return null;
        try {
          const v = JSON.parse(script.innerHTML);
          return { option1: v.option1, option2: v.option2, option3: v.option3 };
        } catch {
          return null;
        }
      }

      getSelectedVariantOptionNames(changedTarget = null) {
        const vs = this.variantSelectors;
        if (!vs) return [];
        const optionWrappers = Array.from(vs.querySelectorAll('.product-form__input')).filter((wrap) => {
          const familyInput = wrap.querySelector('input[name="family_wood"], input[name="family_chairtype"], select[name="family_wood"], select[name="family_chairtype"]');
          return !familyInput;
        });
        const optionNames = [];
        for (const wrap of optionWrappers) {
          let val = null;
          const selectEl = wrap.querySelector('select');
          if (selectEl) {
            if (changedTarget && changedTarget === selectEl) {
              val = selectEl.value;
            } else if (changedTarget && changedTarget.tagName === 'OPTION' && selectEl.contains(changedTarget)) {
              val = changedTarget.value;
            } else {
              val = selectEl.value;
            }
            optionNames.push(val != null ? String(val).trim() : '');
            continue;
          }
          const radios = wrap.querySelectorAll('input[type="radio"]');
          if (radios.length) {
            let checkedRadio = null;
            if (changedTarget && changedTarget.type === 'radio' && wrap.contains(changedTarget)) {
              checkedRadio = changedTarget;
            } else {
              checkedRadio = wrap.querySelector('input[type="radio"]:checked');
            }
            if (checkedRadio) {
              optionNames.push(checkedRadio.value != null ? String(checkedRadio.value).trim() : '');
            }
          }
        }
        return optionNames;
      }

      getVariantIdFromOptionNames(optionNames) {
        if (!optionNames?.length) return null;
        const script = this.querySelector(`#product-variants-${this.sectionId}`);
        if (!script?.textContent) return null;
        let variants = [];
        try {
          variants = JSON.parse(script.textContent.trim());
        } catch {
          return null;
        }
        const n = (s) => (s != null ? String(s).trim() : '');
        const match = variants.find(
          (v) =>
            n(v.option1) === n(optionNames[0]) &&
            n(v.option2) === n(optionNames[1]) &&
            n(v.option3) === n(optionNames[2])
        );
        return match ? match.id : null;
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.disableButtons();
        let productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        let shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;
        const viewMode = this.dataset.viewMode || 'main-product';

        const changedElement = event.target;
        const selectedOptionNames = this.getSelectedVariantOptionNames(changedElement);
        const variantId = this.getVariantIdFromOptionNames(selectedOptionNames);
        const familyWood =
          this.querySelector('input[name="family_wood"]:checked')?.value ||
          this.querySelector('select[name="family_wood"]')?.value ||
          null;
        const familyChairtype =
          this.querySelector('input[name="family_chairtype"]:checked')?.value ||
          this.querySelector('select[name="family_chairtype"]')?.value ||
          null;

        const savedFamilySelection =
          familyWood || familyChairtype ? { family_wood: familyWood, family_chairtype: familyChairtype } : null;

        const isFamilyChange =
          event.target && (event.target.name === 'family_wood' || event.target.name === 'family_chairtype');
        const familyMap = isFamilyChange ? this.getFamilyProductMap() : null;
        const familyKey =
          familyWood && familyChairtype
            ? `${familyWood}|||${familyChairtype}`
            : familyWood
              ? `${familyWood}|||`
              : familyChairtype
                ? `|||${familyChairtype}`
                : '';
        const targetFamilyProduct = familyMap && familyKey ? familyMap[familyKey] : null;
        const targetProductUrl =
          targetFamilyProduct && (targetFamilyProduct.url || targetFamilyProduct.handle)
            ? (targetFamilyProduct.url || `/products/${targetFamilyProduct.handle}`)
            : null;

        if (
          targetFamilyProduct &&
          targetProductUrl &&
          targetProductUrl !== this.dataset.url
        ) {
          const savedOptionValues = this.getCurrentVariantOptionValues();
          this.renderProductInfo({
            requestUrl: `${targetProductUrl}?section_id=${this.sectionId}`,
            targetId: target.id,
            callback: this.handleFamilyProductSwitch(
              targetProductUrl,
              savedOptionValues,
              savedFamilySelection,
              viewMode
            )
          });
          return;
        }

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage, {
            variantId,
            family_wood: familyWood,
            family_chairtype: familyChairtype
          }),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage, viewMode)
            : this.handleUpdateProductInfo(productUrl, viewMode, savedFamilySelection)
        });
      }

      handleFamilyProductSwitch(targetProductUrl, savedOptionValues, savedFamilySelection, viewMode) {
        return (html) => {
          const quickView = html.querySelector('#MainProduct-quick-view__content');
          if (quickView && viewMode === 'quick-view') {
            html = quickView.content.cloneNode(true);
          }

          const newProductInfo = html.querySelector('product-info');
          if (!newProductInfo) {
            this.enableButtons();
            return;
          }

          const variantsScript = html.querySelector(`#product-variants-${this.sectionId}`);
          let variants = [];
          if (variantsScript && variantsScript.textContent) {
            try {
              variants = JSON.parse(variantsScript.textContent.trim());
            } catch (e) {
              console.warn('Family product switch: could not parse variants', e);
            }
          }

          const findMatchingVariant = () => {
            if (!savedOptionValues || !variants.length) return variants[0] || null;
            return (
              variants.find(
                (v) =>
                  v.option1 === savedOptionValues.option1 &&
                  v.option2 === savedOptionValues.option2 &&
                  v.option3 === savedOptionValues.option3
              ) || variants[0] || null
            );
          };

          const variant = findMatchingVariant();
          if (!variant) {
            this.applyFamilyProductSwitchHtml(html, null, newProductInfo, savedFamilySelection, targetProductUrl);
            return;
          }

          const urlWithVariant = `${targetProductUrl}?section_id=${this.sectionId}&variant=${variant.id}`;
          this.abortController?.abort();
          this.abortController = new AbortController();
          fetch(urlWithVariant, { signal: this.abortController.signal })
            .then((response) => response.text())
            .then((responseText) => {
              let htmlWithVariant = new DOMParser().parseFromString(responseText, 'text/html');
              const qv = htmlWithVariant.querySelector('#MainProduct-quick-view__content');
              if (qv && viewMode === 'quick-view') {
                htmlWithVariant = qv.content.cloneNode(true);
              }
              this.applyFamilyProductSwitchHtml(
                htmlWithVariant,
                variant,
                htmlWithVariant.querySelector('product-info'),
                savedFamilySelection,
                targetProductUrl
              );
            })
            .catch((error) => {
              if (error.name === 'AbortError') return;
              console.error(error);
              this.applyFamilyProductSwitchHtml(html, variant, newProductInfo, savedFamilySelection, targetProductUrl);
            })
            .finally(() => this.enableButtons());
        };
      }

      applyFamilyProductSwitchHtml(html, variant, newProductInfo, savedFamilySelection, targetProductUrl) {
        if (!newProductInfo) {
          this.enableButtons();
          return;
        }
        const resolvedVariant = variant || this.getSelectedVariant(html);
        const newProductId = newProductInfo.getAttribute('data-product-id') || this.productId;
        const productUrl = targetProductUrl != null ? targetProductUrl : this.dataset.url;

        HTMLUpdateUtility.viewTransition(
          this,
          newProductInfo,
          this.preProcessHtmlCallbacks,
          this.postProcessHtmlCallbacks
        );

        this.updateURL(productUrl, resolvedVariant?.id);
        this.updateShareUrl(resolvedVariant?.id, productUrl);
        if (resolvedVariant) {
          this.updateVariantInputs(resolvedVariant.id);
          if (!variant) this.syncOptionInputsToVariant(resolvedVariant);
        }

        if (!resolvedVariant) {
          this.setUnavailable();
          this.enableButtons();
          return;
        }

        this.pickupAvailability?.update(resolvedVariant);
        this.updateMedia(resolvedVariant);

        const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
          const source = html.getElementById(`${id}-${this.sectionId}`);
          const destination = this.querySelector(`#${id}-${this.dataset.section}`);
          if (source && destination) {
            destination.innerHTML = source.innerHTML;
            destination.classList.toggle('hidden', shouldHide(source));
          }
        };

        updateSourceFromDestination('price');
        updateSourceFromDestination('Sku');
        updateSourceFromDestination('Inventory');
        updateSourceFromDestination('TextInventory', (source) => source.classList.contains('hidden'));
        updateSourceFromDestination('Badges');
        updateSourceFromDestination('PricePerItem');
        updateSourceFromDestination('Volume');

        this.updateQuantityRules(this.sectionId, newProductId, html);
        updateSourceFromDestination('QuantityRules');
        updateSourceFromDestination('VolumeNote');

        HTMLUpdateUtility.viewTransition(
          document.querySelector(`#SizeChart-${this.sectionId}`),
          html.querySelector(`#SizeChart-${this.sectionId}`),
          this.preProcessHtmlCallbacks,
          this.postProcessHtmlCallbacks
        );

        const addButtonUpdated = html.getElementById(`ProductSubmitButton-${this.sectionId}`);
        this.toggleAddButton(
          addButtonUpdated ? addButtonUpdated.hasAttribute('disabled') : true,
          FoxTheme.variantStrings.soldOut
        );

        const stickyAtcBar = document.getElementById('shopify-section-sticky-atc-bar');
        if (stickyAtcBar) stickyAtcBar.classList.remove('hidden');

        if (savedFamilySelection) this.restoreFamilySelection(savedFamilySelection);

        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, {
          data: { sectionId: this.sectionId, html, variant: resolvedVariant }
        });

        document.dispatchEvent(new CustomEvent('variant:changed', { detail: { variant: resolvedVariant } }));

        this.currentVariant = resolvedVariant;
        this.enableButtons();
      }

      syncOptionInputsToVariant(variant) {
        const variantSelects = this.variantSelectors;
        if (!variantSelects || !variant) return;
        const optionValues = [variant.option1, variant.option2, variant.option3].filter(Boolean);
        const optionGroups = Array.from(variantSelects.querySelectorAll('.product-form__input')).filter(
          (el) =>
            !el.querySelector(
              'input[name="family_wood"], input[name="family_chairtype"], select[name="family_wood"], select[name="family_chairtype"]'
            )
        );
        let optionIndex = 0;
        const norm = (s) => (s != null ? String(s).trim() : '');
        optionGroups.forEach((group) => {
          const val = optionValues[optionIndex];
          if (val == null) return;
          const valNorm = norm(val);
          const sel = group.querySelector('select');
          if (sel) {
            const opt = Array.from(sel.options).find((o) => norm(o.value) === valNorm);
            if (opt) {
              sel.value = opt.value;
              opt.selected = true;
            }
            optionIndex++;
            return;
          }
          const radio = Array.from(group.querySelectorAll('input[type="radio"]')).find(
            (r) => norm(r.value) === valNorm
          );
          if (radio) {
            radio.checked = true;
            const span = group.querySelector('[data-selected-value]');
            if (span) span.textContent = radio.value;
            optionIndex++;
          }
        });
      }

      handleSwapProduct(productUrl, updateFullPage, viewMode) {
        return (html) => {
          const quickView = html.querySelector('#MainProduct-quick-view__content');
          if (quickView && viewMode === 'quick-view') {
            html = quickView.content.cloneNode(true);
          }
          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));

          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;
            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
            HTMLUpdateUtility.viewTransition(
              document.getElementById('shopify-section-sticky-atc-bar'),
              html.getElementById('shopify-section-sticky-atc-bar'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );

            if (!variant) {
              this.setUnavailable();
              return;
            }
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }

          this.currentVariant = variant;
        };
      }

      handleUpdateProductInfo(productUrl, viewMode, savedFamilySelection = null) {
        return (html) => {
          const quickView = html.querySelector('#MainProduct-quick-view__content');
          if (quickView && viewMode === 'quick-view') {
            html = quickView.content.cloneNode(true);
          }
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          if (variant) this.syncOptionInputsToVariant(variant);
          this.updateURL(productUrl, variant?.id);
          this.updateShareUrl(variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(variant);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku');
          updateSourceFromDestination('Inventory');
          updateSourceFromDestination('TextInventory', (source) => source.classList.contains('hidden'));
          updateSourceFromDestination('Badges');
          updateSourceFromDestination('PricePerItem');
          updateSourceFromDestination('Volume');

          this.updateQuantityRules(this.sectionId, this.productId, html);
          updateSourceFromDestination('QuantityRules');
          updateSourceFromDestination('VolumeNote');

          HTMLUpdateUtility.viewTransition(
            document.querySelector(`#SizeChart-${this.sectionId}`),
            html.querySelector(`#SizeChart-${this.sectionId}`),
            this.preProcessHtmlCallbacks,
            this.postProcessHtmlCallbacks
          );

          const addButtonUpdated = html.getElementById(`ProductSubmitButton-${this.sectionId}`);
          this.toggleAddButton(
            addButtonUpdated ? addButtonUpdated.hasAttribute('disabled') : true,
            FoxTheme.variantStrings.soldOut
          );

          const stickyAtcBar = document.getElementById(`shopify-section-sticky-atc-bar`);
          if (stickyAtcBar) {
            stickyAtcBar.classList.remove('hidden');
          }

          if (savedFamilySelection) {
            this.restoreFamilySelection(savedFamilySelection);
          }

          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });

          document.dispatchEvent(
            new CustomEvent('variant:changed', {
              detail: {
                variant: variant,
              },
            })
          );

          this.enableButtons();
        };
      }

      restoreFamilySelection({ family_wood, family_chairtype }) {
        if (family_wood) {
          const sel = this.querySelector('select[name="family_wood"]');
          if (sel && sel.querySelector(`option[value="${CSS.escape(family_wood)}"]`)) {
            sel.value = family_wood;
            const wrapper = sel.closest('.product-form__input');
            const span = wrapper?.querySelector('[data-selected-family-value]');
            if (span) span.textContent = family_wood;
          } else {
            const radio = this.querySelector(`input[name="family_wood"][value="${CSS.escape(family_wood)}"]`);
            if (radio) {
              radio.checked = true;
              const wrapper = radio.closest('.product-form__input');
              const span = wrapper?.querySelector('[data-selected-family-value]');
              if (span) span.textContent = family_wood;
            }
          }
        }
        if (family_chairtype) {
          const sel = this.querySelector('select[name="family_chairtype"]');
          if (sel && sel.querySelector(`option[value="${CSS.escape(family_chairtype)}"]`)) {
            sel.value = family_chairtype;
            const wrapper = sel.closest('.product-form__input');
            const span = wrapper?.querySelector('[data-selected-family-value]');
            if (span) span.textContent = family_chairtype;
          } else {
            const radio = this.querySelector(`input[name="family_chairtype"][value="${CSS.escape(family_chairtype)}"]`);
            if (radio) {
              radio.checked = true;
              const wrapper = radio.closest('.product-form__input');
              const span = wrapper?.querySelector('[data-selected-family-value]');
              if (span) span.textContent = family_chairtype;
            }
          }
        }
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false, extra = {}) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (extra.variantId) {
          params.push(`variant=${extra.variantId}`);
        }

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        if (extra.family_wood) {
          params.push(`family_wood=${encodeURIComponent(extra.family_wood)}`);
        }
        if (extra.family_chairtype) {
          params.push(`family_chairtype=${encodeURIComponent(extra.family_chairtype)}`);
        }

        return `${url}?${params.join('&')}`;
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      updateURL(url, variantId) {
        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      updateVariantInputs(variantId) {
        document
          .querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`)
          .forEach((productForm) => {
            const input = productForm.querySelector('input[name="id"]');
            input.value = variantId ?? '';
            input.dispatchEvent(new Event('change', { bubbles: true }));
          });
      }

      updateMedia(variant) {
        const productMedia = this.querySelector(`[id^="MediaGallery-${this.dataset.section}"]`);
        if (!productMedia) return; // Early return if productMedia is not found

        const setActiveMedia = () => {
          if (typeof productMedia.setActiveMedia === 'function') {
            productMedia.init();
            productMedia.setActiveMedia(variant);
            return true; // Indicate success
          }
          return false; // Indicate failure
        };

        if (!setActiveMedia()) {
          this.timer = setInterval(() => {
            if (setActiveMedia()) {
              clearInterval(this.timer);
            }
          }, 100);
        }
      }

      updateShareUrl(variantId, baseProductUrl) {
        if (!variantId) return;
        const shareButton = document.getElementById(`ProductShare-${this.dataset.section}`);
        if (!shareButton || !shareButton.updateUrl) return;
        const url = baseProductUrl != null ? baseProductUrl : this.dataset.url;
        shareButton.updateUrl(`${window.shopUrl || ''}${url}?variant=${variantId}`);
      }

      toggleAddButton(disable = true, text, modifyClass = true) {
        const productForm = document.getElementById(`product-form-${this.dataset.section}`);
        if (!productForm) return;
        const addButton = productForm.querySelector('[name="add"]');
        const addButtonText = productForm.querySelector('[name="add"] > span');
        if (!addButton) return;

        if (disable) {
          addButton.setAttribute('disabled', 'disabled');
          if (text) addButtonText.textContent = this.decoded(text);
        } else {
          addButton.removeAttribute('disabled');
          addButtonText.textContent = this.decoded(FoxTheme.variantStrings.addToCart);
        }

        if (!modifyClass) return;
      }

      decoded(text) {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = text;
        const decoded = tempDiv.textContent;
        return decoded;
      }

      setUnavailable() {
        this.toggleAddButton(true, FoxTheme.variantStrings.unavailable);
        const price = document.getElementById(`price-${this.dataset.section}`);
        const inventory = document.getElementById(`Inventory-${this.dataset.section}`);
        const textInventory = document.getElementById(`TextInventory-${this.dataset.section}`);
        const sku = document.getElementById(`Sku-${this.dataset.section}`);
        const volumePricing = document.getElementById(`Volume-${this.dataset.section}`);
        const stickyAtcBar = document.getElementById(`shopify-section-sticky-atc-bar`);

        if (price) price.classList.add('hidden');
        if (inventory) inventory.classList.add('hidden');
        if (textInventory) textInventory.classList.add('hidden');
        if (sku) sku.classList.add('hidden');
        if (volumePricing) volumePricing.classList.add('hidden');
        if (stickyAtcBar) stickyAtcBar.classList.add('hidden');
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.setQuantityBoundries();
        if (!this.hasAttribute('data-original-section')) {
          this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(
            FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
            this.fetchQuantityRules.bind(this)
          );
        }
      }

      setQuantityBoundries() {
        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.quantityBoundries, {
          data: {
            sectionId: this.sectionId,
            productId: this.productId,
          },
        });
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.productIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart')?.classList.add('btn--loading');

        fetch(`${this.getAttribute('data-url')}?variant=${currentVariantId}&section_id=${this.sectionId}`)
          .then((response) => response.text())
          .then((responseText) => {
            const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.sectionId, this.productId, parsedHTML);
          })
          .catch((error) => {
            console.error(error);
          })
          .finally(() => {
            this.querySelector('.quantity__rules-cart')?.classList.remove('btn--loading');
          });
      }

      updateQuantityRules(sectionId, productId, parsedHTML) {
        if (!this.quantityInput) return;

        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.quantityRules, {
          data: {
            sectionId,
            productId,
            parsedHTML,
          },
        });

        this.setQuantityBoundries();
      }

      disableButtons() {
        this.toggleButtons(true);
      }

      enableButtons() {
        this.toggleButtons(false);
      }

      toggleButtons(disabled) {
        const buttonSelectors = [
          '[name="add"]',
          '.shopify-payment-button__button',
          '.sticky-atc-bar [name="add"]'
        ];

        buttonSelectors.forEach(selector => {
          const buttons = document.querySelectorAll(selector);
          buttons.forEach(button => {
            if (disabled) {
              button.setAttribute('disabled', 'disabled');
              button.style.pointerEvents = 'none';
              button.style.opacity = '0.6';
            } else {
              if (selector === '[name="add"]') {
                const variantId = button.closest('form')?.querySelector('[name="id"]')?.value;
                if (!variantId) return;
              }
              
              button.removeAttribute('disabled');
              button.style.pointerEvents = '';
              button.style.opacity = '';
            }
          });
        });
      }
    }
  );
}
